/* Order Register duplicate tools: toolbar filters + blinking "Possible duplicate" badge with resolve actions. */
import { PwInput } from "@/components/ui/pw-input";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, CheckCheck, Download, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { API, getAuthHeader } from "@/App";

const pill = (on, onCls, offCls) => `inline-flex items-center gap-1 whitespace-nowrap h-8 px-2.5 rounded border text-[11px] font-semibold transition-colors ${on ? onCls : offCls}`;

export const DupToolbar = ({ dupOnly, setDupOnly, dbaCross, setDbaCross, refreshKey }) => {
  const [st, setSt] = useState(null);
  const [xl, setXl] = useState(false);
  useEffect(() => {
    axios.get(`${API}/order-register/dup-stats`, { headers: getAuthHeader() }).then(r => setSt(r.data)).catch(() => {});
  }, [refreshKey]);
  const excel = async () => {
    setXl(true);
    try {
      const r = await axios.get(`${API}/order-register/duplicates/excel`, { headers: getAuthHeader(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Duplicate_orders_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error("Could not build the duplicates report"); } finally { setXl(false); }
  };
  return (
    <>
      <button type="button" onClick={() => { setDupOnly(v => !v); setDbaCross(false); }} data-testid="or-dup-filter"
        title="Possible duplicate orders still waiting for a decision (same tracking, or no tracking + same customer, products &amp; qty — 70 days)"
        className={pill(dupOnly, "bg-red-600 text-white border-red-600", `bg-white text-red-600 border-red-300 hover:bg-red-50 ${st?.dup_pending ? "animate-pulse" : ""}`)}>
        <AlertTriangle className="w-3.5 h-3.5" /> Duplicate orders ({st?.dup_pending ?? "—"})
        {st?.high_risk > 0 && <span className={`ml-0.5 rounded px-1 ${dupOnly ? "bg-white text-red-700" : "bg-red-600 text-white"}`}>{st.high_risk} high risk</span>}
      </button>
      <button type="button" onClick={() => { setDbaCross(v => !v); setDupOnly(false); }} data-testid="or-dba-cross-filter"
        title="Open (not completed) parcels whose tracking is already in Database A — likely already shipped"
        className={pill(dbaCross, "bg-emerald-600 text-white border-emerald-600", "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50")}>
        <CheckCheck className="w-3.5 h-3.5" /> Database A cross ✓ ({st?.dba_cross ?? "—"})
      </button>
      <button type="button" onClick={excel} disabled={xl} data-testid="or-dup-excel"
        className={pill(false, "", "bg-white text-slate-700 border-slate-300 hover:bg-slate-50 disabled:opacity-60")}>
        {xl ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Duplicates Excel
      </button>
    </>
  );
};

export const DupFlagBadge = ({ row, onDone, readOnly }) => {
  const f = row.dup_flag;
  const [mode, setMode] = useState("");
  const [reason, setReason] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  if (!f || f.status !== "pending") return null;
  const resolve = async (action) => {
    setBusy(true);
    try {
      if (action === "is_dup") {
        if (!reason.trim() || !pwd) { toast.error("Reason and your login password are required"); setBusy(false); return; }
        await axios.post(`${API}/order-register/bulk-cancel`, { ids: [row.id], password: pwd, reason: `Duplicate order — ${reason.trim()}` }, { headers: getAuthHeader() });
      }
      await axios.post(`${API}/order-register/dup-flag/resolve`, { ids: [row.id], action, reason: reason.trim() }, { headers: getAuthHeader() });
      toast.success(action === "is_dup" ? "Marked duplicate — parcel cancelled" : "Cleared — not a duplicate");
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <Popover onOpenChange={(o) => { if (!o) { setMode(""); setPwd(""); } }}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} data-testid={`or-dup-badge-${row.id}`}
          className={`inline-flex items-center gap-0.5 whitespace-nowrap rounded px-1.5 py-0.5 text-[9px] font-bold uppercase animate-pulse ${f.high_risk ? "bg-red-600 text-white" : "bg-orange-100 text-orange-800 border border-orange-300"}`}>
          <AlertTriangle className="w-3 h-3" /> {f.high_risk ? "High risk dup" : "Possible dup"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-3 text-xs" align="start" onClick={(e) => e.stopPropagation()} data-testid={`or-dup-pop-${row.id}`}>
        <div className={`font-bold mb-1 ${f.high_risk ? "text-red-700" : "text-orange-700"}`}>{f.high_risk ? "HIGH RISK — same tracking already in register" : "Possible duplicate — no tracking, same products & qty"}</div>
        <div className="text-slate-600 mb-2">{f.rule}</div>
        <div className="max-h-40 overflow-y-auto rounded border border-slate-200 divide-y divide-slate-100 mb-2">
          {(f.matches || []).map((m, i) => (
            <div key={`${m.id}-${i}`} className="px-2 py-1">
              <b>{m.date}</b> · <span className="font-mono">{m.tracking || "no tracking"}</span>
              <div className="text-slate-600">{m.medicine} × {m.quantity}{m.invoice_no ? ` · Inv ${m.invoice_no}` : ""}</div>
            </div>
          ))}
        </div>
        {readOnly ? <div className="text-[11px] text-slate-500" data-testid={`or-dup-readonly-${row.id}`}>View-only — the processing team resolves duplicates.</div> : mode === "is_dup" ? (
          <div className="space-y-1.5">
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (e.g. re-ordered by mistake)" maxLength={200}
              className="w-full h-8 rounded border border-slate-300 px-2" data-testid={`or-dup-reason-${row.id}`} />
            <PwInput value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Your login password"
              className="w-full h-8 rounded border border-slate-300 px-2" data-testid={`or-dup-pwd-${row.id}`} />
            <button type="button" disabled={busy} onClick={() => resolve("is_dup")} data-testid={`or-dup-confirm-${row.id}`}
              className="w-full h-8 rounded bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-60">Cancel this parcel as duplicate</button>
          </div>
        ) : (
          <div className="flex gap-1.5">
            <button type="button" disabled={busy} onClick={() => resolve("not_dup")} data-testid={`or-dup-not-${row.id}`}
              className="flex-1 h-8 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-60">Not duplicate</button>
            <button type="button" disabled={busy} onClick={() => setMode("is_dup")} data-testid={`or-dup-is-${row.id}`}
              className="flex-1 h-8 rounded bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-60">Is duplicate</button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
